import { Trans, Plural, useLingui } from '@lingui/react/macro';
import { Button } from '@pantry/ui';
import { ArrowUpTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface BatchSummaryBarProps {
  itemCount: number;
  validCount: number;
  isSubmitting: boolean;
  onSubmit: () => void;
  onDiscard: () => void;
}

/**
 * Fixed bottom bar showing batch summary and submit button.
 */
export function BatchSummaryBar({
  itemCount,
  validCount,
  isSubmitting,
  onSubmit,
  onDiscard,
}: BatchSummaryBarProps) {
  const { t } = useLingui();
  const hasInvalidItems = validCount < itemCount;
  const canSubmit = validCount > 0 && !isSubmitting;
  const needAttention = itemCount - validCount;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-background via-background to-background/95 border-t border-border/50 shadow-lg z-40 animate-in slide-in-from-bottom-4 duration-300">
      <div className="max-w-2xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Summary */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">
              <Plural value={itemCount} one="# item in batch" other="# items in batch" />
            </p>
            {hasInvalidItems && (
              <p className="text-xs text-expiring-soon">
                <Plural value={needAttention} one="# needs attention" other="# need attention" />
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="rounded-xl"
              onClick={onDiscard}
              disabled={isSubmitting || itemCount === 0}
            >
              <XMarkIcon className="h-4 w-4 mr-1" />
              <Trans>Discard</Trans>
            </Button>
            <Button
              onClick={onSubmit}
              disabled={!canSubmit}
              className="min-w-[140px] h-11 rounded-xl"
            >
              {isSubmitting ? (
                <>
                  <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  <Trans>Adding...</Trans>
                </>
              ) : (
                <>
                  <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
                  <Plural value={validCount} one="Add # Item" other="Add # Items" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
